import { EndpointPermissionRule } from '../core/permissions/endpoint-permission.models';
import {
  MUHASEBE_FEATURE_PERMISSIONS,
  MUHASEBE_HESAP_PLANI_PERMISSIONS,
  MUHASEBE_YARDIMCI_DEFTER_PERMISSIONS,
  P,
} from './portal-permissions.constants';

/** Ortak lookup — herhangi bir Muhasebe feature read yeterli (şimdilik esnek) */
const MUHASEBE_ALL = [...MUHASEBE_FEATURE_PERMISSIONS];

const MIZAN = [P.Muhasebe.Emisyon];
const HESAP_PLANI = [...MUHASEBE_HESAP_PLANI_PERMISSIONS];
const MUAVIN = [P.Muhasebe.Muavin];
const EF = [P.Muhasebe.EfKutuphanesi];
const SONUCLAR = [P.Muhasebe.Sonuclar];
const STOK = [P.Muhasebe.StokDefteri];
const DURAN = [P.Muhasebe.DuranVarlikDefteri];
const YARDIMCI = [...MUHASEBE_YARDIMCI_DEFTER_PERMISSIONS];
const KONSOL = [P.Muhasebe.Konsolidasyon];

function multiRead(
  pattern: string,
  permissionKeys: string[],
  methods: ('GET' | 'POST')[] = ['GET', 'POST'],
): EndpointPermissionRule[] {
  return methods.map((method) => ({
    method,
    pattern,
    permissionKeys,
    access: 'read' as const,
  }));
}

function multiWrite(
  pattern: string,
  permissionKeys: string[],
  methods: ('GET' | 'POST')[] = ['POST'],
): EndpointPermissionRule[] {
  return methods.map((method) => ({
    method,
    pattern,
    permissionKeys,
    access: 'write' as const,
  }));
}

/**
 * api/AccountingEmission — generator çoğu kaçırır (baseUrl).
 * Ortak lookup'lar MUHASEBE_ALL; ekran API'leri feature key.
 * Yardımcı Defter: StokDefteri / DuranVarlikDefteri ayrı.
 */
export const MUHASEBE_ENDPOINT_EXTRA_RULES: readonly EndpointPermissionRule[] =
  [
    /* --- Ortak --- */
    ...multiRead('api/Company/GetLocations', [
      ...MUHASEBE_ALL,
      P.Yonetim.Lokasyonlar,
    ]),
    ...multiRead('api/AccountingEmission/GetStatuses', MUHASEBE_ALL, ['GET']),
    ...multiRead('api/AccountingEmission/GetErpColumnProfiles', MUHASEBE_ALL, [
      'GET',
    ]),
    ...multiWrite('api/AccountingEmission/SaveErpColumnProfile', MUHASEBE_ALL),
    ...multiRead(
      'api/AccountingEmission/GetApplicationCompanies',
      MUHASEBE_ALL,
      ['GET'],
    ),
    ...multiRead('api/AccountingEmission/GetAccountCodeTree', MUHASEBE_ALL, [
      'GET',
    ]),

    /* --- Mizan / Muhasebe Emisyonu --- */
    ...multiRead('api/AccountingEmission/GetHeaders', MIZAN, ['POST']),
    ...multiRead('api/AccountingEmission/GetDetails', MIZAN, ['GET']),
    ...multiRead('api/AccountingEmission/GetDetailsExcel', MIZAN, ['GET']),
    ...multiRead('api/AccountingEmission/GetExcelColumns', MIZAN, ['GET']),
    ...multiRead('api/AccountingEmission/GetMizanTemplate', MIZAN, [
      'GET',
      'POST',
    ]),
    ...multiRead('api/AccountingEmission/GetHeaderVersions', MIZAN, ['GET']),
    ...multiRead('api/AccountingEmission/GetMappingProgress', MIZAN),
    ...multiWrite('api/AccountingEmission/Upload', MIZAN),
    ...multiWrite('api/AccountingEmission/DeleteHeader', MIZAN),
    ...multiWrite('api/AccountingEmission/DeleteDetail', MIZAN),
    ...multiWrite('api/AccountingEmission/UpdateDetail', MIZAN),
    ...multiWrite('api/AccountingEmission/RunAccountingProcess', MIZAN),
    ...multiWrite('api/AccountingEmission/ReRunExceptions', MIZAN),
    ...multiWrite('api/AccountingEmission/ResolveUnmatchedLines', MIZAN),

    /* --- Hesap Planı (+ legacy Hesap Ağacı) --- */
    ...multiRead('api/AccountingEmission/GetChartOfAccounts', HESAP_PLANI, [
      'GET',
    ]),
    ...multiRead('api/AccountingEmission/GetOrphanedMappings', HESAP_PLANI, [
      'GET',
    ]),
    ...multiRead(
      'api/AccountingEmission/GetVukTemplateSuggestions',
      HESAP_PLANI,
    ),
    ...multiRead('api/AccountingEmission/GetScopeMappings', HESAP_PLANI, [
      'GET',
    ]),
    ...multiRead('api/AccountingEmission/GetMappingRules', HESAP_PLANI, [
      'GET',
    ]),
    ...multiRead('api/AccountingEmission/GetMappingRulesV2', HESAP_PLANI, [
      'GET',
    ]),
    ...multiRead('api/AccountingEmission/GetMappingTable', HESAP_PLANI, [
      'GET',
    ]),
    ...multiRead('api/AccountingEmission/GetSectorDefaults', HESAP_PLANI, [
      'GET',
    ]),
    ...multiRead('api/AccountingEmission/GetNodeDescriptions', HESAP_PLANI, [
      'GET',
    ]),
    ...multiRead('api/AccountingEmission/GetRuleCandidates', HESAP_PLANI, [
      'GET',
    ]),
    ...multiRead('api/AccountingEmission/SuggestSliceEf', HESAP_PLANI),
    ...multiWrite('api/AccountingEmission/CreateAccountNode', HESAP_PLANI),
    ...multiWrite('api/AccountingEmission/UpdateAccountNode', HESAP_PLANI),
    ...multiWrite('api/AccountingEmission/DeleteAccountNode', HESAP_PLANI),
    ...multiWrite('api/AccountingEmission/ReloadChartOfAccounts', HESAP_PLANI),
    ...multiWrite('api/AccountingEmission/DeleteChartOfAccounts', HESAP_PLANI),
    ...multiWrite('api/AccountingEmission/SaveAccountScopeMapping', HESAP_PLANI),
    ...multiWrite('api/AccountingEmission/SaveScopeMapping', HESAP_PLANI),
    ...multiWrite('api/AccountingEmission/SaveMappingRule', HESAP_PLANI),
    ...multiWrite('api/AccountingEmission/SaveMappingRuleV2', HESAP_PLANI),
    ...multiWrite('api/AccountingEmission/DeleteMappingRule', HESAP_PLANI),
    ...multiWrite('api/AccountingEmission/DeleteMappingRuleV2', HESAP_PLANI),
    ...multiWrite('api/AccountingEmission/ToggleExclusion', HESAP_PLANI),
    ...multiWrite('api/AccountingEmission/BulkApplySectorDefaults', HESAP_PLANI),
    ...multiWrite('api/AccountingEmission/PromoteRuleCandidate', HESAP_PLANI),

    /* --- Muavin Merkezi --- */
    ...multiRead('api/AccountingEmission/GetLedgerUploads', MUAVIN, ['GET']),
    ...multiRead('api/AccountingEmission/GetLedgerReconciliation', MUAVIN, [
      'GET',
    ]),
    ...multiRead('api/AccountingEmission/SuggestLedgerAssignments', MUAVIN),
    ...multiWrite('api/AccountingEmission/UploadLedger', MUAVIN),
    ...multiWrite('api/AccountingEmission/ConfirmLedgerAssignments', MUAVIN),

    /* --- Yardımcı: ortak sub-ledger (eski mizan alt akış) — Stok|Duran|Mizan --- */
    ...multiRead(
      'api/AccountingEmission/GetSubLedgerEntries',
      [...YARDIMCI, ...MIZAN],
      ['POST'],
    ),
    ...multiRead(
      'api/AccountingEmission/GetSubLedgerCrossCheck',
      [...YARDIMCI, ...MIZAN],
      ['POST'],
    ),
    ...multiRead(
      'api/AccountingEmission/GetSubLedgerExcelColumns',
      [...YARDIMCI, ...MIZAN],
      ['GET'],
    ),
    ...multiRead('api/AccountingEmission/GetCarveOutPreview', YARDIMCI, [
      'GET',
    ]),
    ...multiWrite('api/AccountingEmission/UploadSubLedger', [
      ...YARDIMCI,
      ...MIZAN,
    ]),
    ...multiWrite('api/AccountingEmission/DeleteSubLedgerEntries', [
      ...YARDIMCI,
      ...MIZAN,
    ]),

    /* --- Yardımcı: Stok Defteri --- */
    ...multiRead('api/AccountingEmission/GetStockLedgerUploads', STOK, ['GET']),
    ...multiRead('api/AccountingEmission/GetStockLedgerLines', STOK, ['GET']),
    ...multiWrite('api/AccountingEmission/UploadStockLedger', STOK),
    ...multiWrite('api/AccountingEmission/SaveMaterialEfLink', [
      ...STOK,
      ...EF,
    ]),
    ...multiWrite('api/AccountingEmission/SaveMaterialEfLinksBulk', [
      ...STOK,
      ...EF,
    ]),

    /* --- Yardımcı: Duran Varlık Defteri --- */
    ...multiRead('api/AccountingEmission/GetFixedAssetUploads', DURAN, [
      'GET',
    ]),
    ...multiRead('api/AccountingEmission/GetFixedAssetLines', DURAN, ['GET']),
    ...multiWrite('api/AccountingEmission/UploadFixedAsset', DURAN),

    /* --- EF Kütüphanesi --- */
    ...multiRead('api/AccountingEmission/SearchEmissionFactors', EF, ['GET']),
    ...multiRead('api/AccountingEmission/GetEfSources', EF, ['GET']),
    ...multiRead('api/AccountingEmission/GetEfDatasetVersions', EF, ['GET']),
    ...multiRead('api/AccountingEmission/GetEmissionFactorsExcel', EF, [
      'GET',
      'POST',
    ]),
    ...multiRead('api/AccountingEmission/GetEmissionFactorTemplate', EF, [
      'GET',
      'POST',
    ]),
    ...multiWrite('api/AccountingEmission/SaveTenantEmissionFactor', EF),
    ...multiWrite('api/AccountingEmission/DeactivateEmissionFactor', EF),
    ...multiWrite('api/AccountingEmission/SaveGlobalEmissionFactor', EF),
    ...multiWrite('api/AccountingEmission/SaveEfSource', EF),
    ...multiWrite('api/AccountingEmission/SaveEfDatasetVersion', EF),
    ...multiWrite('api/AccountingEmission/UploadEmissionFactors', EF),

    /* --- Sonuçlar --- */
    ...multiRead('api/AccountingEmission/GetRuns', SONUCLAR, ['GET', 'POST']),
    ...multiRead('api/AccountingEmission/GetRunResults', SONUCLAR),
    ...multiRead('api/AccountingEmission/GetResultsMatrix', SONUCLAR, ['GET']),
    ...multiRead('api/AccountingEmission/CompareRuns', SONUCLAR, ['GET']),
    ...multiRead('api/AccountingEmission/GetResultDrillDown', SONUCLAR, [
      'GET',
    ]),
    ...multiRead('api/AccountingEmission/GetScopeCompleteness', SONUCLAR),
    ...multiRead('api/AccountingEmission/GetRunDashboard', SONUCLAR, ['GET']),
    ...multiWrite('api/AccountingEmission/FreezeRun', SONUCLAR),

    /* --- Konsolidasyon --- */
    ...multiRead('api/AccountingEmission/GetConsolidationGroups', KONSOL, [
      'GET',
    ]),
    ...multiRead('api/AccountingEmission/GetConsolidationMatrix', KONSOL, [
      'GET',
    ]),
    ...multiWrite('api/AccountingEmission/SaveConsolidationGroup', KONSOL),
    ...multiWrite('api/AccountingEmission/GenerateConsolidationRun', KONSOL),
    ...multiWrite('api/AccountingEmission/SaveEliminationEntry', KONSOL),
    ...multiWrite('api/AccountingEmission/SaveManualAdjustment', KONSOL),
  ];
